import React from "react";
import { useQuery, gql } from "@apollo/client";

// Staff cost month by month from the payroll runs: who was paid, what went
// to salary, commission, leave deductions and adjustments, cash set against
// pay and what was actually transferred. Drafts show as such.

const GET_PAYROLL_TRENDS = gql`
  query GetPayrollTrends($months: Int) {
    getPayrollTrends(months: $months) {
      status
      message
      months {
        month
        year
        status
        people
        salary
        commission
        leaveDeduction
        adjustments
        net
        carriedForward
        cashAdjusted
        transferred
      }
    }
  }
`;

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const label = (m, y) => new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });

const COLS = [
  ["people", "People", (r) => r.people],
  ["salary", "Salary", (r) => inr(r.salary)],
  ["commission", "Commission", (r) => inr(r.commission)],
  ["leaveDeduction", "Leave deductions", (r) => `-${inr(r.leaveDeduction)}`],
  ["adjustments", "Adjustments", (r) => (r.adjustments >= 0 ? "+" : "-") + inr(Math.abs(r.adjustments))],
  ["net", "Net pay", (r) => inr(r.net)],
  ["cashAdjusted", "Cash set off", (r) => inr(r.cashAdjusted)],
  ["transferred", "Transferred", (r) => inr(r.transferred)],
  ["carriedForward", "Carried forward", (r) => inr(r.carriedForward)],
];

const PayrollTrends = () => {
  const { data, loading } = useQuery(GET_PAYROLL_TRENDS, { variables: { months: 12 }, fetchPolicy: "network-only" });
  const res = data?.getPayrollTrends;
  const months = res?.months || [];
  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (res?.status === "ERROR") return <p className="text-sm text-red-600">{res.message}</p>;
  if (!months.length) return <p className="text-sm text-gray-500">No payroll runs yet.</p>;
  const latest = months[months.length - 1];
  const prev = months.length > 1 ? months[months.length - 2] : null;
  const change = prev && prev.net ? Math.round(((latest.net - prev.net) / prev.net) * 100) : null;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        {label(latest.month, latest.year)}: net pay <b>{inr(latest.net)}</b> to {latest.people} people
        {change !== null && ` (${change >= 0 ? "+" : ""}${change}% vs ${label(prev.month, prev.year)})`}.
        Last 12 runs, oldest first. Drafts change until they are locked.
      </p>
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-left text-sm text-gray-600">
          <thead className="bg-gray-50 text-gray-700 border-b">
            <tr>
              <th className="py-2 px-3">Month</th>
              {COLS.map(([k, h]) => (
                <th key={k} className="py-2 px-3 text-right whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {months.map((r) => (
              <tr key={`${r.year}-${r.month}`}>
                <td className="py-2 px-3 whitespace-nowrap text-gray-800">
                  {label(r.month, r.year)}
                  {r.status !== "PAID" && <span className="ml-1 text-[11px] text-gray-400">{r.status.toLowerCase()}</span>}
                </td>
                {COLS.map(([k, , show]) => (
                  <td key={k} className={`py-2 px-3 text-right whitespace-nowrap ${k === "net" ? "font-semibold text-gray-800" : ""}`}>
                    {show(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollTrends;
